import { Controller } from "@hotwired/stimulus";
import { Modifier } from "@popperjs/core";
import tippy, { type Instance } from "tippy.js";

export default class extends Controller<HTMLDivElement> {
  static targets = ["trigger", "menu"];

  declare readonly triggerTarget: HTMLDivElement;
  declare readonly menuTarget: HTMLDivElement;

  declare tippy: Instance;

  declare maxWidthValue: number;
  declare maxHeightValue: number;
  declare triggerValue: string;
  declare arrowValue: boolean;
  declare autoWidthValue: boolean;
  declare themeValue: string;
  declare animationValue: string;
  declare durationValue: number[];
  declare offsetValue: number[];
  declare hideOnClickValue: boolean;
  declare placementValue: string;
  declare showOnCreateValue: boolean;

  static values = {
    maxWidth: { type: Number, default: 350 },
    trigger: { type: String, default: "mouseenter" },
    arrow: { type: Boolean, default: false },
    autoWidth: { type: Boolean, default: false },
    theme: { type: String, default: "light-border" },
    animation: { type: String, default: "shift-toward-extreme" },
    duration: { type: Array, default: [300, 250] },
    offset: { type: Array, default: [0, 8] },
    hideOnClick: { type: Boolean, default: true },
    placement: { type: String, default: "bottom-start" },
    showOnCreate: { type: Boolean, default: false }
  };

  connect() {
    const content = document.createElement("div");

    this.menuTarget.parentNode?.insertBefore(content, this.menuTarget);
    const menu = this.menuTarget;

    const maxWidth = this.maxWidthValue;

    const handleElementFocusOut = this.handleElementFocusOut.bind(this);
    const element = this.element;

    // class=dark tippy-box tippy-content
    this.tippy = tippy(this.triggerTarget, {
      theme: this.themeValue,
      zIndex: 25,
      offset: this.offsetValue,
      arrow: this.arrowValue,
      placement: this.placementValue,
      hideOnClick: this.hideOnClickValue,
      showOnCreate: this.showOnCreateValue,
      interactive: true,
      allowHTML: true,
      trigger: this.triggerValue,
      maxWidth,
      animation: this.animationValue,
      duration: window.__TEST_MODE__ ? [0, 0] : this.durationValue,
      content,
      popperOptions: {
        modifiers: this.popperModifiers()
      },
      onCreate: () => {
        this.element.dataset.dropdownReady = "true";
      },
      onShow: (() => {
        element.addEventListener("focusout", handleElementFocusOut);
        content.append(menu);
        if (menu.innerHTML.trim() === "") {
          return false;
        }
        this.dispatch("show");
      }).bind(this),
      onShown: (() => {
        content.dataset.shown = "true";
        this.dispatch("shown");
      }).bind(this),
      onHidden: (() => {
        element.removeEventListener("focusout", handleElementFocusOut);
        element.dispatchEvent(new CustomEvent("dropdown:hidden"));
        delete content.dataset.shown;
        element.append(menu);
        this.dispatch("hidden");
      }).bind(this),
      onHide: (() => {
        element.dispatchEvent(new CustomEvent("dropdown:hide"));
        this.dispatch("hide");
      }).bind(this),
      onClickOutside(instance) {
        element.removeEventListener("focusout", handleElementFocusOut);
        instance.hide();
      }
    });
    // Remove the element form the DOM to prevent having duplicated form
    // fields. Specially for the filters controls
    // this.menuTarget.remove();

    // to let our tests know when this element have been correctly initialized
    this.triggerTarget.dataset.testDropdownReady = "true";
  }

  hide() {
    this.tippy.hide();
  }

  popperModifiers() {
    const modifiers: Modifier[] = [];
    if (this.autoWidthValue) {
      modifiers.push({
        name: "sameWidth",
        enabled: true,
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        phase: "beforeWrite",
        requires: ["computeStyles"],
        effect: ({ state }) => {
          state.elements.popper.style.width = `${
            (state.elements.reference as Element).clientWidth
          }px`;
        }
      });
    }

    return modifiers;
  }

  handleElementFocusOut() {
    setTimeout(() => {
      if (this.element.contains(document.activeElement)) {
        return;
      }
      this.tippy.hide();
    });
  }
}
